import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowUpRight, Check, ShieldCheck, Cloud, Fingerprint, Lock, Stamp,
  Layers, Database, Server, Network, Cog, KeyRound, FileSearch, Sparkles,
  AlertTriangle, Cpu, Users, EyeOff, MapPin, Globe, Zap, Clock, GitBranch,
  RefreshCw, Building2, Package, ArrowRight, Boxes, TerminalSquare, Camera,
  ClipboardList, Wrench, ChevronRight, Loader2
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import api from '../lib/api';

const problems = [
  { icon: Boxes, title: 'Multi-Cloud Complexity', desc: 'Managing separate backup tools for each cloud provider creates operational overhead and inconsistency.' },
  { icon: Lock, title: 'Vendor Lock-In', desc: 'Cloud-native backup solutions trap your data, making cross-cloud recovery impossible.' },
  { icon: Layers, title: 'Fragmented Tools', desc: 'Different interfaces, policies, and workflows across AWS Backup, Azure Backup, GCP snapshots, M365 backup, and Google Workspace Vault.' },
  { icon: AlertTriangle, title: 'Recovery Failures', desc: 'When disaster strikes, missing dependencies and broken relationships cause restore failures across infrastructure and SaaS data.' }
];

const capabilities = [
  { icon: Cloud, title: 'One Platform for Clouds & SaaS', desc: 'Single unified interface to backup and restore across Azure, AWS, GCP, OCI, Microsoft 365, Google Workspace, and Intune.' },
  { icon: Cog, title: 'Policy-Driven Backups', desc: 'Define once, apply everywhere. Automated schedules, retention, and compliance rules for infrastructure and SaaS data.' },
  { icon: RefreshCw, title: 'One-Click Restore', desc: 'Recovery plans with dependency awareness ensure nothing breaks during restoration of cloud resources and SaaS content.' },
  { icon: GitBranch, title: 'Cross-Cloud Recovery', desc: 'Restore Azure workloads to AWS, or any cloud to any cloud. Break vendor lock-in with cross-cloud translation.' },
  { icon: Zap, title: 'Fast Recovery', desc: 'Sub-15 minute RTO with parallel restore and intelligent resource prioritization for infrastructure and SaaS data.' },
  { icon: Network, title: 'Dependency-Aware Restore', desc: 'Topological sorting ensures resources restore in the correct order with intact relationships across cloud and SaaS.' },
  { icon: ShieldCheck, title: 'Secure & Encrypted', desc: 'AES-256-GCM encryption, BYOK support, and Azure Key Vault integration for enterprise security across all platforms.' },
  { icon: FileSearch, title: 'Observability & Insights', desc: 'Real-time dashboards, OpenTelemetry tracing, and capability reports for every recovery point across engines.' },
  { icon: Layers, title: 'Isolated Execution Engines', desc: 'Separate Cloud DR and SaaS Backup engines with isolated runtimes, queues, and database schemas for security.' }
];

const lifecycle = [
  {
    n: '01',
    icon: Camera,
    title: 'Capture',
    desc: 'The edge, running inside your cloud, records each resource, its configuration, a dependency graph of how everything connects, and the data — sealed into one signed manifest.'
  },
  {
    n: '02',
    icon: ClipboardList,
    title: 'Plan',
    desc: 'You pick a target: same place, another region, another subscription or tenant, or another cloud. CloudRebuild builds an immutable, validated restore plan ordered by dependency.'
  },
  {
    n: '03',
    icon: Wrench,
    title: 'Rebuild',
    desc: 'The edge recreates the resources in the target, in the right order, and reports honest, receipt-backed progress at every step.'
  }
];

const architecture = [
  { n: '01', icon: ShieldCheck, title: 'Zero-custody by architecture', desc: 'The vendor plane only plans and signs. Your data and long-lived credentials never leave your cloud — CloudRebuild holds neither.' },
  { n: '02', icon: KeyRound, title: 'Your storage, your keys', desc: 'Backups land in your own storage (BYOS), encrypted with your keys (BYOK, AES-256-GCM). You own the storage bill — which structurally removes lock-in.' },
  { n: '03', icon: Globe, title: 'Sovereign by design', desc: 'UAE North primary with Central India DR, and PDPL, SAMA and NDMO treated as design properties — not a configuration you bolt on afterwards.' },
  { n: '04', icon: Stamp, title: 'Signed, outbound-only control plane', desc: 'Edges make only outbound 443 connections, verify JWS-signed work-tickets addressed to them, use mTLS with 24-hour certificates, and store no secrets.' },
  { n: '05', icon: Zap, title: 'Serverless, scale-to-zero', desc: 'No appliances, proxies, or always-on VMs. Workers wake on a signed ticket and scale back to zero — you pay when you protect, not to keep infrastructure idling.' },
  { n: '06', icon: Building2, title: 'MSP-native white-label', desc: 'Built for Microsoft CSP and GDAP delivery, so partners resell CloudRebuild under their own brand with hard multi-tenant isolation.' }
];

const clouds = ['Azure', 'AWS', 'GCP', 'OCI'];

const saas = [
  { title: 'Microsoft 365', tag: 'Complete protection for your Microsoft 365 environment', items: ['Exchange Online', 'SharePoint', 'OneDrive', 'Teams'] },
  { title: 'Google Workspace', tag: 'Secure backup for your Google Workspace data', items: ['Gmail', 'Drive', 'Calendar', 'Shared Drives'] },
  { title: 'Microsoft Intune', tag: 'Protect your mobile device management configurations', items: ['Device Configs', 'Compliance Policies', 'App Protection', 'User Assignments'] }
];

const restorePaths = [
  { tag: 'IN-PLACE RECOVERY', title: 'In-place recovery', desc: 'Restores a resource over the original, in the same subscription and region — back to exactly the captured configuration, not whatever drifted since.', when: 'Undo corruption, a bad change, ransomware, or a failed update.' },
  { tag: 'SAME REGION · SIDE-BY-SIDE', title: 'Same region, side-by-side', desc: 'Creates a new, parallel resource alongside the original in the same region. The original is left untouched.', when: 'Restore drills, granular recovery of a single disk or file, or a clone for testing.' },
  { tag: 'CROSS REGION', title: 'Cross region', desc: 'Replays the dependency graph in a different region: disks, NICs, subnets, security groups and public IPs first, then the compute.', when: 'Regional outage DR, data-residency moves, or moving a workload closer to users.' },
  { tag: 'CROSS SUBSCRIPTION', title: 'Cross subscription', desc: 'Lands the same rebuild in another subscription under the same tenant.', when: 'Re-architecting subscriptions, splitting prod from non-prod, or new billing boundaries.' },
  { tag: 'CROSS TENANT', title: 'Cross tenant', desc: 'Rebuilds into a different Entra ID tenant — something native billing transfers often cannot do, because a CSP subscription’s tenant cannot be changed.', when: 'Mergers, acquisitions, divestitures, or moving out of a partner tenant into your own.' },
  { tag: 'CROSS CLOUD', title: 'Cross cloud', desc: 'One Universal Resource Model translates a workload captured in one cloud into another cloud’s equivalents and rebuilds it there.', when: 'Cloud exit or entry, multi-cloud DR, or avoiding a single-provider dependency.' }
];

const useCases = [
  { icon: ShieldCheck, title: 'Disaster Recovery', desc: 'Recover entire workloads — VMs, databases, networks, IAM — across clouds with full dependency awareness.' },
  { icon: ArrowRight, title: 'Multi-Cloud Migration', desc: 'Move workloads between clouds without rebuilding from scratch. Translate IAM, networking, and storage automatically.' },
  { icon: FileSearch, title: 'Compliance & Backup', desc: 'Meet regulatory requirements with automated retention, audit trails, and immutable backup manifests.' },
  { icon: TerminalSquare, title: 'Dev/Test Environments', desc: 'Clone production environments to any cloud for testing, development, or troubleshooting.' }
];

const editions = [
  {
    tier: 'Essentials',
    highlight: false,
    items: ['Cloud backup', 'In-place, same-region and cross-region restore', 'Core reporting']
  },
  {
    tier: 'Professional',
    highlight: true,
    tag: 'MOST DEPLOYED',
    items: ['Everything in Essentials, plus', 'Cross-subscription and cross-tenant rebuild', 'Microsoft 365 and Intune packs', 'DR drills']
  },
  {
    tier: 'Enterprise',
    highlight: false,
    items: ['Everything in Professional, plus', 'Cross-cloud rebuild', 'Residency-enforced autonomous DR', 'Clean-room recovery', 'Advanced compliance evidence']
  }
];

const pricingUnits = [
  { title: 'Cloud platform', unit: 'Per protected resource, per month', desc: 'Azure / AWS / GCP / OCI — priced by resource type. A VM, a database, a disk each carry their own rate.' },
  { title: 'Microsoft 365', unit: 'Per user, per month', desc: 'Exchange Online, OneDrive, SharePoint and Teams in one unit.' },
  { title: 'Google Workspace', unit: 'Per user, per month', desc: 'Gmail, Drive, Calendar and Shared Drives.' },
  { title: 'Microsoft Intune', unit: 'Per organization, per month', desc: 'One flat charge covering the tenant’s configuration backup.' },
  { title: 'Storage', unit: 'You own it (BYOS)', desc: 'Billed by your own cloud provider, not by CloudRebuild.' }
];

const CLOUD_OPTIONS = ['Azure', 'AWS', 'GCP', 'OCI'];

const DemoForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    full_name: '',
    work_email: '',
    company: '',
    job_title: '',
    phone: '',
    message: '',
    clouds: [],
    website: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const toggleCloud = (c) => {
    setForm((f) => ({
      ...f,
      clouds: f.clouds.includes(c) ? f.clouds.filter((x) => x !== c) : [...f.clouds, c]
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.work_email.trim() || !form.company.trim()) {
      toast({ title: 'Please fill in name, work email and company' });
      return;
    }
    // honeypot
    if (form.website) return;

    setSubmitting(true);
    try {
      await api.post('/demo-requests', { ...form, source: 'cloudrebuild' });
      setDone(true);
      toast({ title: 'Request received', description: 'Our team will reach out shortly.' });
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: err?.response?.data?.detail || 'Please try again in a moment.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="card-dark rounded-3xl p-10 text-center">
        <div className="h-14 w-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
          <Check className="text-emerald-300" size={26} />
        </div>
        <div className="text-white text-xl font-display font-semibold mb-2">Thanks — we&apos;ve got it.</div>
        <p className="text-slate-400 max-w-md mx-auto text-sm">
          A CloudRebuild specialist will reach out within one business day to schedule your walkthrough.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card-dark rounded-3xl p-6 md:p-8 space-y-5" data-testid="cr-demo-form">
      {/* honeypot */}
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={form.website}
        onChange={(e) => setForm({ ...form, website: e.target.value })}
        className="hidden"
        aria-hidden="true"
      />

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-slate-400 mb-1.5 block">Full name *</label>
          <Input
            data-testid="cr-form-name"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className="bg-[#0a1020] border-white/10 text-white h-11 rounded-xl"
            placeholder="Jane Doe"
            required
          />
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1.5 block">Work email *</label>
          <Input
            data-testid="cr-form-email"
            type="email"
            value={form.work_email}
            onChange={(e) => setForm({ ...form, work_email: e.target.value })}
            className="bg-[#0a1020] border-white/10 text-white h-11 rounded-xl"
            placeholder="jane@company.com"
            required
          />
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1.5 block">Company *</label>
          <Input
            data-testid="cr-form-company"
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
            className="bg-[#0a1020] border-white/10 text-white h-11 rounded-xl"
            placeholder="Acme Corp"
            required
          />
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1.5 block">Job title</label>
          <Input
            data-testid="cr-form-title"
            value={form.job_title}
            onChange={(e) => setForm({ ...form, job_title: e.target.value })}
            className="bg-[#0a1020] border-white/10 text-white h-11 rounded-xl"
            placeholder="Head of Cloud"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs text-slate-400 mb-1.5 block">Phone (optional)</label>
          <Input
            data-testid="cr-form-phone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="bg-[#0a1020] border-white/10 text-white h-11 rounded-xl"
            placeholder="+1 555 000 0000"
          />
        </div>
      </div>

      <div>
        <label className="text-xs text-slate-400 mb-2 block">Which cloud providers are you using?</label>
        <div className="flex flex-wrap gap-2">
          {CLOUD_OPTIONS.map((c) => {
            const active = form.clouds.includes(c);
            return (
              <button
                type="button"
                key={c}
                onClick={() => toggleCloud(c)}
                data-testid={`cr-form-cloud-${c.toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                  active
                    ? 'bg-amber-500/20 text-amber-200 border-amber-500/40'
                    : 'bg-white/5 text-slate-300 border-white/10 hover:border-amber-500/30'
                }`}
              >
                {active && <Check size={14} className="inline mr-1.5 -mt-0.5" />}
                {c}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="text-xs text-slate-400 mb-1.5 block">Tell us about your backup and DR needs</label>
        <Textarea
          data-testid="cr-form-message"
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          className="bg-[#0a1020] border-white/10 text-white rounded-xl min-h-[110px]"
          placeholder="Workloads, target clouds, timelines, compliance requirements…"
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
        <p className="text-xs text-slate-500 max-w-sm">
          A specialist will reach out within one business day. We&apos;ll never share your details.
        </p>
        <Button
          type="submit"
          disabled={submitting}
          data-testid="cr-form-submit"
          className="btn-gradient rounded-full h-11 px-6 border-0"
        >
          {submitting ? (
            <><Loader2 size={16} className="mr-2 animate-spin" /> Sending…</>
          ) : (
            <>Request a demo <ArrowUpRight size={16} className="ml-1" /></>
          )}
        </Button>
      </div>
    </form>
  );
};

const CloudRebuildPage = () => {
  return (
    <main className="bg-[#05080f] min-h-screen" data-testid="cloudrebuild-page">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-36 pb-20 md:pt-44 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.18),transparent_60%)]"></div>
        <div className="perspective-grid"></div>

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-300 text-sm mb-6">
            <ArrowLeft size={14} /> Back to home
          </Link>
          <div className="section-label mb-6">Product · CloudRebuild</div>
          <div className="text-xs tracking-[0.3em] text-amber-300/80 mb-3">MULTI-CLOUD DISASTER RECOVERY PLATFORM</div>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-semibold leading-[1.03] tracking-tight text-white max-w-4xl">
            Rebuild any cloud. <span className="gradient-text">Anywhere.</span>
          </h1>
          <p className="mt-6 text-slate-300 max-w-2xl text-base md:text-lg leading-relaxed">
            Unified backup and disaster recovery across multi-cloud and SaaS environments —
            from Azure to Microsoft 365, AWS to Google Workspace. One platform. One policy.
            Any cloud, any region, any tenant.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#demo">
              <Button className="btn-gradient rounded-full h-11 px-6 border-0" data-testid="cr-demo-btn">
                Request a demo <ArrowUpRight size={16} className="ml-1" />
              </Button>
            </a>
            <a href="#capabilities">
              <Button variant="outline" className="rounded-full h-11 px-6 bg-white/5 border-white/10 text-white hover:bg-white/10">
                Explore capabilities
              </Button>
            </a>
          </div>

          {/* Source -> Target diagram */}
          <div className="mt-14 card-dark rounded-3xl p-6 md:p-8">
            <div className="grid md:grid-cols-3 gap-6 items-center">
              <div>
                <div className="text-[11px] tracking-widest text-slate-500 mb-3">SOURCE</div>
                <div className="flex flex-wrap gap-2">
                  {clouds.map((c) => (
                    <span key={c} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-200 text-sm">{c}</span>
                  ))}
                </div>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-amber-500/10 border border-amber-500/25">
                  <Package className="text-amber-300" size={18} />
                  <span className="text-amber-200 font-semibold text-sm">CloudRebuild Backup Engine</span>
                </div>
              </div>
              <div className="md:text-right">
                <div className="text-[11px] tracking-widest text-slate-500 mb-3">RESTORE</div>
                <div className="flex md:justify-end flex-wrap gap-2">
                  {['Same Cloud', 'Different Cloud', 'Multi-Region'].map((c) => (
                    <span key={c} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-200 text-sm">{c}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEMS */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">Why cloud backup breaks</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-12">
            The multi-cloud <span className="gradient-text">resilience gap.</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {problems.map((p) => (
              <div key={p.title} className="card-dark rounded-3xl p-6 hover:border-amber-500/30 transition-colors">
                <div className="h-11 w-11 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center mb-4">
                  <p.icon className="text-amber-400" size={20} strokeWidth={1.5} />
                </div>
                <h3 className="text-white font-semibold mb-2">{p.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section id="capabilities" className="relative py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">What CloudRebuild delivers</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-12">
            One platform. <span className="gradient-text">Every cloud.</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {capabilities.map((c) => (
              <div key={c.title} className="card-dark rounded-3xl p-6 hover:border-amber-500/30 transition-colors">
                <div className="h-12 w-12 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center mb-4">
                  <c.icon className="text-amber-400" size={22} strokeWidth={1.5} />
                </div>
                <h3 className="text-white text-lg font-semibold mb-2">{c.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIFECYCLE - animated Capture → Plan → Rebuild */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">How it works</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-4">
            Capture. Plan. <span className="gradient-text">Rebuild.</span>
          </h2>
          <p className="text-slate-400 max-w-3xl leading-relaxed mb-14">
            That is what lets CloudRebuild rebuild a whole environment in dependency order — and even translate and rebuild it into a different cloud — instead of just handing back a disk image.
          </p>

          {/* Diagram */}
          <div className="relative">
            {/* animated dashed connector (desktop) */}
            <div className="pointer-events-none hidden md:block absolute inset-x-0 top-[68px] h-px z-0" aria-hidden="true">
              <div className="mx-auto w-[86%] h-full relative">
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-amber-500/40 to-transparent"></div>
                <div className="absolute inset-0 cr-flow-stream"></div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 md:gap-4 relative z-10">
              {lifecycle.map((s, idx) => (
                <div key={s.n} className="relative">
                  {/* connector arrow (mobile-only stacked) */}
                  {idx > 0 && (
                    <div className="md:hidden flex justify-center mb-4 -mt-2">
                      <ChevronRight className="text-amber-400 rotate-90" size={22} />
                    </div>
                  )}

                  <div className="card-dark rounded-3xl p-7 hover:border-amber-500/40 transition-colors relative">
                    {/* icon node on the wire */}
                    <div className="relative mx-auto w-[136px] h-[136px] mb-5">
                      <div className="absolute inset-0 rounded-full bg-amber-500/10 border border-amber-500/25 cr-node-pulse"></div>
                      <div className="absolute inset-3 rounded-full bg-[#0a1020] border border-amber-500/30 flex items-center justify-center">
                        <s.icon className="text-amber-300" size={40} strokeWidth={1.4} />
                      </div>
                      <div className="absolute -top-2 -right-2 h-8 w-8 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-[#0b1220] font-display font-semibold text-sm flex items-center justify-center shadow-lg shadow-amber-500/30">
                        {s.n}
                      </div>
                    </div>

                    <h3 className="text-white text-xl font-display font-semibold mb-2 text-center">{s.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed text-center">{s.desc}</p>
                  </div>

                  {/* desktop chevron between cards */}
                  {idx < lifecycle.length - 1 && (
                    <div className="hidden md:flex absolute top-[68px] -right-4 z-20 items-center justify-center">
                      <div className="h-9 w-9 rounded-full bg-[#05080f] border border-amber-500/30 flex items-center justify-center">
                        <ChevronRight className="text-amber-300 cr-chevron-slide" size={18} />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* diagram-only CSS keyframes */}
          <style>{`
            @keyframes cr-flow {
              0%   { background-position: 0% 50%; opacity: 0.4; }
              50%  { opacity: 1; }
              100% { background-position: 200% 50%; opacity: 0.4; }
            }
            .cr-flow-stream {
              background-image: linear-gradient(90deg, transparent 0%, rgba(212,175,55,0.9) 30%, rgba(212,175,55,0.4) 50%, rgba(212,175,55,0.9) 70%, transparent 100%);
              background-size: 200% 100%;
              animation: cr-flow 3.5s linear infinite;
              filter: blur(0.5px);
            }
            @keyframes cr-pulse {
              0%, 100% { transform: scale(1); opacity: 0.55; }
              50%      { transform: scale(1.08); opacity: 0.9; }
            }
            .cr-node-pulse { animation: cr-pulse 2.8s ease-in-out infinite; }
            @keyframes cr-chev {
              0%   { transform: translateX(-4px); opacity: 0.4; }
              50%  { transform: translateX(2px);  opacity: 1; }
              100% { transform: translateX(-4px); opacity: 0.4; }
            }
            .cr-chevron-slide { animation: cr-chev 1.6s ease-in-out infinite; }
          `}</style>
        </div>
      </section>

      {/* ARCHITECTURE (6 pillars) */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">Architecture</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-4">
            Six architectural moats <span className="gradient-text">no conventional SaaS can retrofit.</span>
          </h2>
          <p className="text-slate-400 max-w-3xl leading-relaxed mb-12">
            Sovereignty, credential-less operation and clone-first DR fall out of the topology — not from a checkbox on a settings page.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {architecture.map((a) => (
              <div key={a.n} className="card-dark rounded-3xl p-6 hover:border-amber-500/30 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-11 w-11 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center">
                    <a.icon className="text-amber-400" size={20} strokeWidth={1.5} />
                  </div>
                  <span className="text-amber-300 font-display font-semibold text-lg">{a.n}</span>
                </div>
                <h3 className="text-white text-lg font-semibold mb-2">{a.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SAAS COVERAGE */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">SaaS coverage</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-12">
            Beyond infrastructure — <span className="gradient-text">SaaS is a first-class citizen.</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {saas.map((s) => (
              <div key={s.title} className="card-dark rounded-3xl p-6 hover:border-amber-500/30 transition-colors">
                <div className="text-white text-lg font-semibold mb-2">{s.title}</div>
                <div className="text-slate-400 text-sm mb-4 leading-relaxed">{s.tag}</div>
                <div className="flex flex-wrap gap-2">
                  {s.items.map((i) => (
                    <span key={i} className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-slate-300 text-xs">{i}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 card-dark rounded-3xl p-6 md:p-8">
            <div className="text-white text-lg font-semibold mb-2">Microsoft Intune — configuration backup</div>
            <p className="text-slate-400 text-sm leading-relaxed">
              For Intune, CloudRebuild backs up your management configuration rather than device data — so a fragile,
              hand-built estate becomes something you can version, audit and rebuild on demand. Every backup is
              versioned so you can roll back a bad change, restore deleted policies, or redeploy the whole
              configuration into another tenant — clean, merged, or dev/test.
            </p>
          </div>
        </div>
      </section>

      {/* RESTORE PATHS */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">Restore paths</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-12">
            Restore anywhere — <span className="gradient-text">same cloud or a different one.</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {restorePaths.map((r) => (
              <div key={r.title} className="card-dark rounded-3xl p-6 hover:border-amber-500/30 transition-colors">
                <div className="text-[11px] tracking-widest text-amber-300 mb-2">{r.tag}</div>
                <h3 className="text-white text-lg font-semibold mb-2">{r.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">{r.desc}</p>
                <div className="pt-3 border-t border-white/5">
                  <div className="text-[11px] tracking-widest text-slate-500 mb-1">WHEN YOU USE IT</div>
                  <div className="text-slate-300 text-sm">{r.when}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">Use cases</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-12">
            Recovery is <span className="gradient-text">just the beginning.</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {useCases.map((u) => (
              <div key={u.title} className="card-dark rounded-3xl p-6 hover:border-amber-500/30 transition-colors">
                <div className="h-11 w-11 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center mb-4">
                  <u.icon className="text-amber-400" size={20} strokeWidth={1.5} />
                </div>
                <div className="text-white font-semibold mb-2">{u.title}</div>
                <div className="text-slate-400 text-sm leading-relaxed">{u.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECURITY + STATS */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">Security &amp; scale</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-12">
            Enterprise-grade, <span className="gradient-text">by construction.</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            <div className="card-dark rounded-3xl p-6">
              <ShieldCheck className="text-amber-400 mb-4" size={22} />
              <div className="text-white text-lg font-semibold mb-3">Enterprise-Grade Security</div>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li className="flex gap-2"><Check size={16} className="text-amber-400 mt-0.5 shrink-0" /> AES-256-GCM encryption at rest and in transit</li>
                <li className="flex gap-2"><Check size={16} className="text-amber-400 mt-0.5 shrink-0" /> Azure Key Vault integration with BYOK support</li>
                <li className="flex gap-2"><Check size={16} className="text-amber-400 mt-0.5 shrink-0" /> Zero-trust architecture with least-privilege access</li>
                <li className="flex gap-2"><Check size={16} className="text-amber-400 mt-0.5 shrink-0" /> SOC 2 Type II compliant infrastructure</li>
              </ul>
            </div>
            <div className="card-dark rounded-3xl p-6">
              <Server className="text-amber-400 mb-4" size={22} />
              <div className="text-white text-lg font-semibold mb-3">Built for Scale</div>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li className="flex gap-2"><Check size={16} className="text-amber-400 mt-0.5 shrink-0" /> Handle terabyte to petabyte-scale workloads</li>
                <li className="flex gap-2"><Check size={16} className="text-amber-400 mt-0.5 shrink-0" /> Parallel backup and restore for performance</li>
                <li className="flex gap-2"><Check size={16} className="text-amber-400 mt-0.5 shrink-0" /> Multi-region storage with automatic replication</li>
                <li className="flex gap-2"><Check size={16} className="text-amber-400 mt-0.5 shrink-0" /> Horizontal scaling for enterprise demands</li>
              </ul>
            </div>
            <div className="card-dark rounded-3xl p-6">
              <Lock className="text-amber-400 mb-4" size={22} />
              <div className="text-white text-lg font-semibold mb-3">99.99% Reliability Design</div>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li className="flex gap-2"><Check size={16} className="text-amber-400 mt-0.5 shrink-0" /> Immutable recovery points prevent tampering</li>
                <li className="flex gap-2"><Check size={16} className="text-amber-400 mt-0.5 shrink-0" /> Automatic integrity verification on every backup</li>
                <li className="flex gap-2"><Check size={16} className="text-amber-400 mt-0.5 shrink-0" /> Redundant storage across availability zones</li>
                <li className="flex gap-2"><Check size={16} className="text-amber-400 mt-0.5 shrink-0" /> 24/7 monitoring with automated failover</li>
              </ul>
            </div>
          </div>

          <div className="mt-8 card-dark rounded-3xl p-6 md:p-8 grid grid-cols-3 gap-6 text-center">
            <div>
              <div className="font-display text-3xl md:text-4xl font-semibold gradient-text">99.99%</div>
              <div className="text-slate-400 text-xs mt-1 tracking-widest">UPTIME SLA</div>
            </div>
            <div>
              <div className="font-display text-3xl md:text-4xl font-semibold gradient-text">&lt; 15 min</div>
              <div className="text-slate-400 text-xs mt-1 tracking-widest">RECOVERY TIME</div>
            </div>
            <div>
              <div className="font-display text-3xl md:text-4xl font-semibold gradient-text">PB-scale</div>
              <div className="text-slate-400 text-xs mt-1 tracking-widest">DATA PROTECTED</div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">Pricing</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-12">
            Transparent units. <span className="gradient-text">You own the storage.</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mb-12">
            {pricingUnits.map((p) => (
              <div key={p.title} className="card-dark rounded-2xl p-5">
                <div className="text-white font-semibold text-sm">{p.title}</div>
                <div className="text-amber-300 text-xs mt-1 mb-3">{p.unit}</div>
                <div className="text-slate-400 text-xs leading-relaxed">{p.desc}</div>
              </div>
            ))}
          </div>

          <div className="section-label mb-6">Editions</div>
          <p className="text-slate-400 leading-relaxed max-w-3xl mb-8">
            Capabilities are packaged into tiers so you license only what you need. Names and inclusions are set per offer — this is the representative structure.
          </p>
          <div className="grid md:grid-cols-3 gap-5">
            {editions.map((e) => (
              <div
                key={e.tier}
                className={`card-dark rounded-3xl p-6 relative ${e.highlight ? 'border-amber-500/40 bg-gradient-to-b from-amber-500/5 to-transparent' : ''}`}
              >
                {e.tag && (
                  <div className="absolute -top-3 left-6 text-[10px] tracking-widest px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
                    {e.tag}
                  </div>
                )}
                <div className="text-white text-xl font-display font-semibold mb-4">{e.tier}</div>
                <ul className="space-y-2 text-slate-300 text-sm">
                  {e.items.map((i) => (
                    <li key={i} className="flex gap-2">
                      <Check size={16} className="text-amber-400 mt-0.5 shrink-0" />
                      <span>{i}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + INLINE DEMO FORM */}
      <section id="demo" className="relative py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="section-label mb-6">Request a demo</div>
              <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-4">
                Ready to protect your <span className="gradient-text">cloud infrastructure?</span>
              </h2>
              <p className="text-slate-300 leading-relaxed mb-6">
                Talk to our team to learn how CloudRebuild can secure your mission-critical workloads across every cloud and every SaaS.
              </p>
              <ul className="space-y-3 text-slate-300 text-sm">
                <li className="flex gap-2"><Check size={16} className="text-amber-400 mt-0.5 shrink-0" /> Live walkthrough tailored to your Azure, AWS, GCP or OCI estate</li>
                <li className="flex gap-2"><Check size={16} className="text-amber-400 mt-0.5 shrink-0" /> A capability report on your first recovery point in under a week</li>
                <li className="flex gap-2"><Check size={16} className="text-amber-400 mt-0.5 shrink-0" /> Multi-cloud, cross-tenant and cross-cloud restore scenarios</li>
                <li className="flex gap-2"><Check size={16} className="text-amber-400 mt-0.5 shrink-0" /> BYOS + BYOK model — you own the storage and the keys</li>
              </ul>
              <div className="mt-8 flex items-center gap-2 text-xs text-slate-500">
                <Sparkles className="text-amber-300" size={14} />
                Trusted by teams protecting terabyte- to petabyte-scale workloads.
              </div>
            </div>

            <DemoForm />
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default CloudRebuildPage;
